import React, { useMemo, useState, useEffect } from 'react';
import { Card, Table, Pagination, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import {
  FaCheckCircle,
  FaPauseCircle,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCog,
} from 'react-icons/fa';

import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { formatBRL, formatDataBR, ITENS_POR_PAGINA } from '../helpers';

// Uso de react-bootstrap Table direto (não TabelaDinamica) porque PRD §4.7 exige
// fundo verde claro em linhas Categoria=0 — TabelaDinamica usa cor selecionada
// fixa (#cce5ff). Sort/paginação inline replicam o padrão do W-0879 (ConversaoLeads).

const COLUNAS = [
  { key: 'codigo_conta', titulo: 'Conta', align: 'left', ordenacao: true },
  {
    key: 'apelido_conta',
    titulo: 'Apelido conta',
    align: 'left',
    ordenacao: true,
  },
  { key: 'numdoc', titulo: 'NumDoc', align: 'left', ordenacao: true },
  { key: 'categoria', titulo: 'Cat.', align: 'right', ordenacao: true },
  { key: 'descricao', titulo: 'Descrição', align: 'left', ordenacao: true },
  { key: 'valor', titulo: 'Valor', align: 'right', ordenacao: true },
  { key: 'dc', titulo: 'D/C', align: 'center', ordenacao: true },
  { key: 'dt_contabil', titulo: 'Dt. Contábil', align: 'center', ordenacao: true },
  {
    key: 'conta_relacionada',
    titulo: 'Conta rel.',
    align: 'right',
    ordenacao: true,
  },
  {
    key: 'contrapartida',
    titulo: 'Contrapartida',
    align: 'right',
    ordenacao: true,
  },
  {
    key: 'centro_custo',
    titulo: 'CC',
    align: 'right',
    ordenacao: true,
  },
  { key: 'saldo', titulo: 'Saldo', align: 'right', ordenacao: true },
  { key: 'lanca', titulo: 'Lança', align: 'center', ordenacao: true },
];

const rowClassName = (mov) => {
  // R11 — Categoria=0 = sem configuração de contrapartida → destaque verde claro.
  if (mov.categoria === 0) return 'table-success';
  return '';
};

const renderLanca = (lanca) => {
  if (lanca === 'S') {
    return (
      <FaCheckCircle
        style={{ color: '#28a745' }}
        aria-label="Pronto para lançar"
        title="Pronto para lançar"
      />
    );
  }
  return (
    <FaPauseCircle
      style={{ color: '#dc3545' }}
      aria-label="Não será lançado"
      title="Não será lançado"
    />
  );
};

const renderValor = (mov) => {
  const valor = mov.valor != null ? Number(mov.valor) : 0;
  const cor = mov.dc === 'D' ? '#155724' : '#721c24';
  return (
    <span style={{ color: cor, fontWeight: 500 }}>{formatBRL(valor)}</span>
  );
};

const ordenarMovimentos = (movimentos, ordenacao) => {
  if (!ordenacao.coluna) return movimentos;
  const copia = [...movimentos];
  copia.sort((a, b) => {
    const valorA = a[ordenacao.coluna] != null ? a[ordenacao.coluna] : '';
    const valorB = b[ordenacao.coluna] != null ? b[ordenacao.coluna] : '';
    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
    }
    const strA = String(valorA).toLowerCase();
    const strB = String(valorB).toLowerCase();
    if (strA < strB) return ordenacao.direcao === 'asc' ? -1 : 1;
    if (strA > strB) return ordenacao.direcao === 'asc' ? 1 : -1;
    return 0;
  });
  return copia;
};

const iconeOrdenacao = (coluna, ordenacao) => {
  if (ordenacao.coluna !== coluna) return <FaSort className="ml-1 text-muted" />;
  return ordenacao.direcao === 'asc' ? (
    <FaSortUp className="ml-1" />
  ) : (
    <FaSortDown className="ml-1" />
  );
};

const renderPaginacao = (paginaAtual, totalPaginas, setPagina) => {
  if (totalPaginas <= 1) return null;
  const maxBotoes = 5;
  let inicio = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
  if (fim - inicio < maxBotoes - 1) {
    inicio = Math.max(1, fim - maxBotoes + 1);
  }
  const itens = [];
  itens.push(
    <Pagination.First
      key="first"
      onClick={() => setPagina(1)}
      disabled={paginaAtual === 1}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      onClick={() => setPagina(paginaAtual - 1)}
      disabled={paginaAtual === 1}
    />
  );
  if (inicio > 1) {
    itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
  }
  for (let pagina = inicio; pagina <= fim; pagina += 1) {
    itens.push(
      <Pagination.Item
        key={`pag-${pagina}`}
        active={pagina === paginaAtual}
        onClick={() => setPagina(pagina)}
      >
        {pagina}
      </Pagination.Item>
    );
  }
  if (fim < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
  }
  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => setPagina(paginaAtual + 1)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => setPagina(totalPaginas)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  return <Pagination className="mb-0">{itens}</Pagination>;
};

const GridMovimentos = ({ movimentos, onConfigurarLinha, onToggleLanca }) => {
  const [ordenacao, setOrdenacao] = useState({
    coluna: 'ordem',
    direcao: 'asc',
  });
  const [pagina, setPagina] = useState(1);
  const [filtroConta, setFiltroConta] = useState(null);

  // LICOES §1.4 — limpar filtro/mudança de dados reseta paginação.
  useEffect(() => {
    setPagina(1);
  }, [filtroConta, movimentos.length]);

  const contasFiltro = useMemo(() => {
    const set = new Map();
    movimentos.forEach((m) => {
      if (m.codigo_conta != null && !set.has(m.codigo_conta)) {
        set.set(
          m.codigo_conta,
          `${m.codigo_conta}${m.apelido_conta ? ` - ${m.apelido_conta}` : ''}`
        );
      }
    });
    return Array.from(set.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [movimentos]);

  const movimentosFiltrados = useMemo(() => {
    if (filtroConta == null) return movimentos;
    return movimentos.filter((m) => m.codigo_conta === filtroConta);
  }, [movimentos, filtroConta]);

  const ordenados = useMemo(
    () => ordenarMovimentos(movimentosFiltrados, ordenacao),
    [movimentosFiltrados, ordenacao]
  );

  const totalPaginas = Math.max(
    1,
    Math.ceil(ordenados.length / ITENS_POR_PAGINA)
  );
  const paginaSegura = Math.min(pagina, totalPaginas);
  const inicio = (paginaSegura - 1) * ITENS_POR_PAGINA;
  const fim = inicio + ITENS_POR_PAGINA;
  const dadosPaginados = ordenados.slice(inicio, fim);

  const trocarOrdenacao = (coluna) => {
    setOrdenacao((atual) => {
      if (atual.coluna === coluna) {
        return {
          coluna,
          direcao: atual.direcao === 'asc' ? 'desc' : 'asc',
        };
      }
      return { coluna, direcao: 'asc' };
    });
  };

  return (
    <Card className="mb-3">
      <Card.Header className="bg-light">
        <Row className="align-items-center">
          <Col xs={12} md={6}>
            <strong>Movimentos da sessão</strong>{' '}
            <span className="text-muted small">
              ({movimentosFiltrados.length}{' '}
              {movimentosFiltrados.length === 1 ? 'movimento' : 'movimentos'})
            </span>
          </Col>
          <Col xs={12} md={6}>
            <Row className="align-items-center">
              <Col xs={8} md={9}>
                <Select
                  options={contasFiltro}
                  value={
                    contasFiltro.find((c) => c.value === filtroConta) || null
                  }
                  onChange={(opt) =>
                    setFiltroConta(opt ? opt.value : null)
                  }
                  placeholder="Todas as contas bancárias"
                  styles={selectStylesSm}
                  isClearable
                />
              </Col>
              <Col xs={4} md={3}>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={() => setFiltroConta(null)}
                  disabled={filtroConta == null}
                >
                  Limpar
                </button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Header>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              {COLUNAS.map((col) => (
                <th
                  key={`col-${col.key}`}
                  style={{
                    cursor: col.ordenacao ? 'pointer' : 'default',
                    textAlign: col.align,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() =>
                    col.ordenacao ? trocarOrdenacao(col.key) : null
                  }
                >
                  {col.titulo}
                  {col.ordenacao ? iconeOrdenacao(col.key, ordenacao) : null}
                </th>
              ))}
              <th className="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {dadosPaginados.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUNAS.length + 1}
                  className="text-center text-muted py-3"
                >
                  Nenhum movimento.
                </td>
              </tr>
            ) : (
              dadosPaginados.map((mov) => (
                <tr
                  key={`mov-${mov.codigo_grupo_empresa}-${mov.codigo_conta}-${mov.ordem}`}
                  className={rowClassName(mov)}
                >
                  <td>{mov.codigo_conta}</td>
                  <td>{mov.apelido_conta || '-'}</td>
                  <td>{mov.numdoc || '-'}</td>
                  <td className="text-right">{mov.categoria}</td>
                  <td>{mov.descricao}</td>
                  <td className="text-right">{renderValor(mov)}</td>
                  <td className="text-center">{mov.dc}</td>
                  <td className="text-center">{formatDataBR(mov.dt_contabil)}</td>
                  <td className="text-right">
                    {mov.conta_relacionada != null
                      ? mov.conta_relacionada
                      : '-'}
                  </td>
                  <td className="text-right">
                    {mov.contrapartida != null ? mov.contrapartida : '-'}
                  </td>
                  <td className="text-right">
                    {mov.centro_custo != null ? mov.centro_custo : '-'}
                  </td>
                  <td className="text-right">
                    {mov.saldo != null ? formatBRL(mov.saldo) : '-'}
                  </td>
                  <td className="text-center">
                    {mov.descricao === 'SALDO ATUAL' ? (
                      <span className="text-muted small">—</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() =>
                          onToggleLanca && onToggleLanca(mov)
                        }
                        title={
                          mov.lanca === 'S'
                            ? 'Clique para desmarcar lançamento'
                            : 'Clique para marcar para lançamento'
                        }
                      >
                        {renderLanca(mov.lanca)}
                      </button>
                    )}
                  </td>
                  <td className="text-center">
                    {mov.descricao === 'SALDO ATUAL' ? null : (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() =>
                          onConfigurarLinha && onConfigurarLinha(mov)
                        }
                        title="Configurar contrapartida"
                      >
                        <FaCog />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <Card.Footer className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          {ordenados.length > 0
            ? `Mostrando ${inicio + 1} até ${Math.min(
                fim,
                ordenados.length
              )} de ${ordenados.length} movimento(s)`
            : 'Sem movimentos'}
        </div>
        {renderPaginacao(paginaSegura, totalPaginas, setPagina)}
      </Card.Footer>
    </Card>
  );
};

export default GridMovimentos;
